/**
 * Finished-goods board (order lens) on the factory floor home — QC-passed output
 * across every producing order, reconciled against order qty, plus the
 * company-wide cut-pieces (by-products) rollup. The output-side complement to
 * the station queues above it.
 *
 * @see packages/domain/src/garment/wip/compute-finished-goods.ts
 */
import { Link } from "@tanstack/react-router";
import { PackageCheck } from "lucide-react";
import type { wip as wipContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";

type Board = wipContract.FinishedGoodsViewWire;
type Row = Board["rows"][number];

export function FinishedGoodsBoard() {
  const q = trpc.wip.finishedGoods.useQuery();
  if (q.isLoading || !q.data) return null;
  const { rows, cutPieces, totals } = q.data;
  if (rows.length === 0) return null;

  return (
    <section className="mt-8">
      <div className="flex items-center gap-2">
        <PackageCheck size={18} className="text-ok" aria-hidden />
        <h2 className="text-base font-semibold text-ink">Finished goods</h2>
      </div>
      <p className="mt-1 text-sm text-muted">QC-passed output across every order — what's done and what's ready to ship.</p>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Ordered" value={totals.orderTotal} />
        <Stat label="Finished · QC-passed" value={totals.finishedTotal} tone="text-ok" />
        <Stat label="Packed" value={totals.packedTotal} />
        <Stat label="Ready to dispatch" value={totals.readyToDispatch} suffix=" orders" tone={totals.readyToDispatch > 0 ? "text-ok" : undefined} />
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-line bg-paper">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
              <th className="px-3 py-2 text-left font-medium">Order</th>
              <th className="px-2 py-2 text-right font-medium">Order qty</th>
              <th className="px-2 py-2 text-right font-medium">Finished</th>
              <th className="px-2 py-2 text-right font-medium">Packed</th>
              <th className="px-2 py-2 text-right font-medium">To finish</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <FinishedRow key={r.styleId} row={r} />
            ))}
          </tbody>
        </table>
      </div>

      {cutPieces.length > 0 ? (
        <div className="mt-3 rounded-lg border border-line bg-bg p-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">Cut-pieces · by-products (all orders)</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs text-ink-2">
            {cutPieces.map((c) => (
              <span key={`${c.kind}-${c.uom}`} className="rounded-full border border-line bg-paper px-2 py-0.5">
                {c.kind.replace(/_/g, " ")}: {c.qty.toLocaleString("en-IN")} {c.uom}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function FinishedRow({ row }: { row: Row }) {
  const pct = row.orderQty > 0 ? Math.min(100, Math.round((row.finished / row.orderQty) * 100)) : 0;
  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-3 py-2">
        <Link to="/production/styles/$styleId/production" params={{ styleId: row.styleId }} className="font-medium text-ink hover:text-sky-700">
          {row.styleRef}
        </Link>
        <div className="text-xs text-muted">{row.buyerName}</div>
      </td>
      <td className="px-2 py-2 text-right font-mono text-ink-2">{row.orderQty.toLocaleString("en-IN")}</td>
      <td className="px-2 py-2 text-right font-mono font-semibold text-ok">{row.finished.toLocaleString("en-IN")}</td>
      <td className="px-2 py-2 text-right font-mono text-ink-2">{row.packed.toLocaleString("en-IN")}</td>
      <td className={`px-2 py-2 text-right font-mono ${row.balanceToFinish > 0 ? "text-warn" : "text-muted"}`}>
        {row.balanceToFinish > 0 ? row.balanceToFinish.toLocaleString("en-IN") : "—"}
      </td>
      <td className="px-3 py-2">
        <StatusBadge row={row} pct={pct} />
      </td>
    </tr>
  );
}

function StatusBadge({ row, pct }: { row: Row; pct: number }) {
  const dispatchLink = (label: string, cls: string) => (
    <Link
      to="/merchandising/styles/$styleId/dispatch"
      params={{ styleId: row.styleId }}
      className={`rounded-full px-2 py-0.5 text-xs font-semibold transition hover:brightness-95 ${cls}`}
    >
      {label}
    </Link>
  );

  // Actionable worklist: a live dispatch trumps the order status, then finish state.
  if (row.dispatchStatus === "confirmed") return dispatchLink("Dispatched →", "bg-bg text-muted");
  if (row.dispatchStatus === "draft") return dispatchLink("Resume dispatch →", "bg-amber-50 text-warn");
  if (row.status === "shipped" || row.status === "closed") {
    return <span className="rounded-full bg-bg px-2 py-0.5 text-xs font-semibold text-muted">Shipped</span>;
  }
  if (row.complete) return dispatchLink("Prepare dispatch →", "bg-emerald-50 text-ok");
  return <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">{pct}% finished</span>;
}

function Stat({ label, value, suffix = "", tone }: { label: string; value: number; suffix?: string; tone?: string | undefined }) {
  return (
    <div className="rounded-lg border border-line bg-paper p-3">
      <div className="text-xs uppercase tracking-wider text-muted">{label}</div>
      <div className={`mt-0.5 font-mono text-xl font-bold ${tone ?? "text-ink"}`}>
        {value.toLocaleString("en-IN")}
        {suffix ? <span className="text-xs font-normal text-muted">{suffix}</span> : null}
      </div>
    </div>
  );
}
